import ApiService from 'api-client/ApiService'
import { ServiceState } from 'app/plugins/infrastructure/components/common/service-health/constant'

class Glance extends ApiService {
  public getClassName() {
    return 'glance'
  }

  protected async getEndpoint() {
    return this.client.keystone.getServiceEndpoint('glance', 'public')
    // return this.client.keystone.getServiceEndpoint('glance', 'admin') // use this when using sbux-scaletest
  }

  // v2 = () => `/v2`

  imagesUrl = () => `/api/glance/v2/images`
  hostUrl = () => {
    return this.client.keystone.getServiceEndpoint('glance', 'admin')
  }

  getImages = async () => {
    const response = await this.client.basicGet<any>({
      url: `/v2/images?limit=1000`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getImages',
      },
    })
    return response.images
  }

  getImagesWithName = async (name) => {
    const response = await this.client.basicGet<any>({
      url: `/v2/images?name=in:"${name}"`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getImagesWithName',
      },
    })
    return response.images
  }

  async getImage(id) {
    const response = await this.client.basicGet<any>({
      url: `/v2/images/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'getImage',
      },
    })
    return response
  }

  async createImage(body) {
    const response = await this.client.basicPost<any>({
      url: `/v2/images`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'createImage',
      },
    })
    return response
  }

  async uploadImage(
    { id, body },
    options?: { onUploadProgress?: (progressEvent: any) => void; signal?: AbortController },
  ) {
    const hostUrl = await this.hostUrl()
    const config = options?.onUploadProgress
      ? {
          onUploadProgress: (progressEvent) => {
            options.onUploadProgress(progressEvent)
          },
        }
      : {}
    if (options?.signal) {
      config['signal'] = options.signal
    }
    try {
      const response = await this.client.basicPut<any>({
        endpoint: hostUrl,
        url: `/v2/images/${id}/file`,
        body,
        options: {
          clsName: this.getClassName(),
          mthdName: 'uploadImage',
          config: {
            headers: {
              'Content-Type': 'application/octet-stream',
            },
            timeout: 36000000, // 10 hours
            ...config,
          },
        },
      })
      return response
    } catch (error) {
      throw error
    }
  }

  async getHostGlanceEndpoint() {
    // Retrieve it each time, because a user may auth / deauth an image library and then the
    // keystone catalog changes
    // Also use the glance admin endpoint instead of glance-cluster bc glance-cluster can be multiple
    const endpoint = await this.client.keystone.getServiceEndpoint('glance', 'admin', true)
    return endpoint
  }

  async testHostGlanceAccess(endpoint?: string) {
    const hostUrl = endpoint ? endpoint : await this.getHostGlanceEndpoint()
    const response = await this.client.basicGet<any>({
      endpoint: hostUrl,
      url: '/v2/images?limit=1',
      options: {
        clsName: this.getClassName(),
        mthdName: 'testHostGlance',
        config: {
          timeout: 1000 * 15, // 15 seconds
        },
      },
    })
    return response
  }

  async importImage({ id, body }) {
    const hostUrl = await this.hostUrl()
    return this.client.basicPost<any>({
      endpoint: hostUrl,
      url: `/v2/images/${id}/import`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'importImage',
      },
    })
  }

  patchImage = async ({ id, body }) => {
    const response = await this.client.basicPatch<any>({
      url: `/v2/images/${id}`,
      body,
      options: {
        clsName: this.getClassName(),
        mthdName: 'patchImage',
        config: {
          headers: {
            'Content-Type': 'application/openstack-images-v2.1-json-patch',
          },
        },
      },
    })
    return response
  }

  deleteImage = async (id) => {
    return this.client.basicDelete<any>({
      url: `/v2/images/${id}`,
      options: {
        clsName: this.getClassName(),
        mthdName: 'deleteImage',
      },
    })
  }

  // This function checks the health of the glance service by making a GET request to the glance endpoint
  getImageServiceHealth = async (endpoint: string) => {
    try {
      await this.client.basicGet<void>({
        endpoint,
        url: '/v2/images?limit=1',
        options: {
          clsName: this.getClassName(),
          mthdName: 'getGlanceServiceHealth',
        },
      })
      return { state: ServiceState.Up, message: 'Glance service is healthy' }
    } catch (error) {
      console.error('Error', error)
      // Incase of network error (Certificate not accepted),
      // OR if the user is note connected to VPN
      // return network_error

      if (error?.err?.message === 'Network Error') {
        return {
          state: ServiceState.CertificateError,
          message: 'Please trust the image library certificate.',
        }
      } else if (error?.err?.code === 'ECONNABORTED') {
        return { state: ServiceState.NetworkError, message: 'Glance image host not reachable' }
      }

      return { state: ServiceState.Down, message: error?.message || 'Unknown error' }
    }
  }
}

export default Glance
